import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  deleteTooltipOverride,
  getTooltips,
  parseTooltipListResponse,
  parseTooltipSeedResponse,
  putTooltips,
  seedTooltips,
} from "../../../tooltips/tooltipApi";
import {
  flattenTooltipCatalog,
  formatTooltipAdminLabel,
  formatTooltipFieldLabel,
  formatTooltipGroupLabel,
  getTooltipGroupKeys,
  TOOLTIP_LABEL_ARROW,
} from "../../../tooltips/tooltipCatalog";
import type { TooltipItem } from "../../../tooltips/types";
import { useTooltips } from "../../../tooltips/TooltipContext";
import AlertComponent from "../../subcomponents/AlertComponent";
import "../../styles/tooltipAdminPage.css";

type TooltipAdminPageProps = {
  /** Locked to the current dashboard product session. */
  productCode: string;
};

type DraftRow = TooltipItem & { draftText: string; dirty: boolean };

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;

function catalogFallbackRows(code: string): DraftRow[] {
  return flattenTooltipCatalog(code).map((i) => ({
    productCode: i.productCode,
    groupKey: i.groupKey,
    fieldKey: i.fieldKey,
    defaultText: i.defaultText,
    text: i.defaultText,
    hasOverride: false,
    draftText: i.defaultText,
    dirty: false,
  }));
}

/**
 * Catalog is the source of truth for which rows appear for this product.
 * API overrides are merged onto matching keys only — never show Golf rows on SnC, etc.
 */
function mergeCatalogWithApi(
  code: string,
  apiItems: TooltipItem[],
): DraftRow[] {
  const catalog = flattenTooltipCatalog(code);
  const ownedGroups = new Set(getTooltipGroupKeys(code));
  const apiByKey = new Map<string, TooltipItem>();

  for (const item of apiItems) {
    if (!ownedGroups.has(item.groupKey)) continue;
    // If API stamps productCode, it must match this session product
    if (
      item.productCode &&
      item.productCode !== code &&
      item.productCode.trim() !== ""
    ) {
      continue;
    }
    apiByKey.set(`${item.groupKey}::${item.fieldKey}`, item);
  }

  return catalog.map((c) => {
    const api = apiByKey.get(`${c.groupKey}::${c.fieldKey}`);
    if (api) {
      return {
        productCode: code,
        groupKey: c.groupKey,
        fieldKey: c.fieldKey,
        defaultText: api.defaultText || c.defaultText,
        text: api.text || c.defaultText,
        hasOverride: Boolean(api.hasOverride),
        updatedAt: api.updatedAt,
        updatedByUsername: api.updatedByUsername,
        draftText: api.text || c.defaultText,
        dirty: false,
      };
    }
    return {
      productCode: code,
      groupKey: c.groupKey,
      fieldKey: c.fieldKey,
      defaultText: c.defaultText,
      text: c.defaultText,
      hasOverride: false,
      draftText: c.defaultText,
      dirty: false,
    };
  });
}

function TooltipBreadcrumb({
  groupKey,
  fieldKey,
}: {
  groupKey: string;
  fieldKey: string;
}) {
  return (
    <span
      className="tooltip-admin-breadcrumb"
      title={formatTooltipAdminLabel(groupKey, fieldKey)}
    >
      <span className="tooltip-admin-crumb tooltip-admin-crumb--group">
        {formatTooltipGroupLabel(groupKey, fieldKey)}
      </span>
      <span className="tooltip-admin-crumb-arrow" aria-hidden="true">
        {TOOLTIP_LABEL_ARROW}
      </span>
      <span className="tooltip-admin-crumb tooltip-admin-crumb--field">
        {formatTooltipFieldLabel(fieldKey, groupKey)}
      </span>
    </span>
  );
}

export default function TooltipAdminPage({ productCode }: TooltipAdminPageProps) {
  const { refresh: refreshRuntime } = useTooltips();
  const [search, setSearch] = useState("");
  const [screenFilter, setScreenFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(10);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [needsSeed, setNeedsSeed] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [openAlert, setOpenAlert] = useState(false);

  const showAlert = (message: string, type: "success" | "error") => {
    setAlertMessage(message);
    setAlertType(type);
    setOpenAlert(true);
  };

  const loadRows = useCallback(async (code: string) => {
    setLoading(true);
    setNeedsSeed(false);
    try {
      const response = await getTooltips(code);
      if (response.status === 404 || response.status === 501) {
        setRows(catalogFallbackRows(code));
        setNeedsSeed(true);
        showAlert(
          "Tooltip API unavailable — showing static defaults. Seed after backend is ready.",
          "error",
        );
        return;
      }
      const parsed = await parseTooltipListResponse(response);
      if (!parsed) {
        setRows(catalogFallbackRows(code));
        setNeedsSeed(true);
        showAlert(
          `Failed to load tooltips for ${code} (HTTP ${response.status}). Using static fallback.`,
          "error",
        );
        return;
      }
      if (parsed.items.length === 0) {
        const fallback = catalogFallbackRows(code);
        setRows(fallback);
        setNeedsSeed(fallback.length > 0);
        return;
      }
      // Always drive the list from this product's catalog (ignore foreign API groups)
      const merged = mergeCatalogWithApi(code, parsed.items);
      setRows(merged);
      const anyFromApi = parsed.items.some(
        (i) =>
          getTooltipGroupKeys(code).includes(i.groupKey) &&
          (!i.productCode || i.productCode === code),
      );
      setNeedsSeed(!anyFromApi && merged.length > 0);
    } catch (err) {
      setRows(catalogFallbackRows(code));
      setNeedsSeed(true);
      showAlert(
        err instanceof Error ? err.message : "Failed to load tooltips",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearch("");
    setScreenFilter("");
    setPage(1);
    void loadRows(productCode);
  }, [productCode, loadRows]);

  useEffect(() => {
    setPage(1);
  }, [search, screenFilter, pageSize]);

  const screenOptions = useMemo(() => {
    const screens = new Set<string>();
    for (const r of rows) {
      screens.add(formatTooltipGroupLabel(r.groupKey, r.fieldKey));
    }
    return Array.from(screens).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      const screenLabel = formatTooltipGroupLabel(r.groupKey, r.fieldKey);
      if (screenFilter && screenLabel !== screenFilter) return false;
      if (!q) return true;
      const label = formatTooltipAdminLabel(
        r.groupKey,
        r.fieldKey,
      ).toLowerCase();
      const groupLabel = screenLabel.toLowerCase();
      const fieldLabel = formatTooltipFieldLabel(
        r.fieldKey,
        r.groupKey,
      ).toLowerCase();
      return (
        label.includes(q) ||
        groupLabel.includes(q) ||
        fieldLabel.includes(q) ||
        r.groupKey.toLowerCase().includes(q) ||
        r.fieldKey.toLowerCase().includes(q) ||
        r.draftText.toLowerCase().includes(q) ||
        r.defaultText.toLowerCase().includes(q)
      );
    });

    return [...list].sort((a, b) => {
      const screenA = formatTooltipGroupLabel(a.groupKey, a.fieldKey);
      const screenB = formatTooltipGroupLabel(b.groupKey, b.fieldKey);
      const byScreen = screenA.localeCompare(screenB);
      if (byScreen !== 0) return byScreen;
      return formatTooltipFieldLabel(a.fieldKey, a.groupKey).localeCompare(
        formatTooltipFieldLabel(b.fieldKey, b.groupKey),
      );
    });
  }, [rows, search, screenFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const rangeStart =
    filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, filtered.length);

  const dirtyCount = rows.filter((r) => r.dirty).length;

  const setDraft = (groupKey: string, fieldKey: string, draftText: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.groupKey !== groupKey || r.fieldKey !== fieldKey) return r;
        const dirty = draftText !== r.text;
        return { ...r, draftText, dirty };
      }),
    );
  };

  const handleSave = async () => {
    const updates = rows
      .filter((r) => r.dirty)
      .map((r) => ({
        groupKey: r.groupKey,
        fieldKey: r.fieldKey,
        text: r.draftText.trim(),
      }));
    if (updates.length === 0) return;
    const empty = updates.find((u) => !u.text);
    if (empty) {
      showAlert(
        `Empty text not allowed (${formatTooltipAdminLabel(empty.groupKey, empty.fieldKey)}). Use Reset to default instead.`,
        "error",
      );
      return;
    }
    setSaving(true);
    try {
      const response = await putTooltips({ productCode, updates });
      if (!response.ok) {
        showAlert(
          response.status === 403
            ? "Forbidden — implementation team, TPM, or admin required."
            : `Save failed (HTTP ${response.status}).`,
          "error",
        );
        return;
      }
      showAlert(`Saved ${updates.length} tooltip(s) for ${productCode}.`, "success");
      await loadRows(productCode);
      await refreshRuntime();
    } catch (err) {
      showAlert(
        err instanceof Error ? err.message : "Save failed",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (row: DraftRow) => {
    setSaving(true);
    try {
      if (row.hasOverride) {
        const response = await deleteTooltipOverride({
          productCode,
          groupKey: row.groupKey,
          fieldKey: row.fieldKey,
        });
        if (!response.ok) {
          showAlert(
            response.status === 403
              ? "Forbidden — implementation team, TPM, or admin required."
              : `Reset failed (HTTP ${response.status}).`,
            "error",
          );
          return;
        }
      }
      setRows((prev) =>
        prev.map((r) =>
          r.groupKey === row.groupKey && r.fieldKey === row.fieldKey
            ? {
                ...r,
                draftText: r.defaultText,
                text: r.defaultText,
                hasOverride: false,
                dirty: false,
              }
            : r,
        ),
      );
      showAlert(
        `Reset ${formatTooltipAdminLabel(row.groupKey, row.fieldKey)} to default.`,
        "success",
      );
      await refreshRuntime();
    } catch (err) {
      showAlert(
        err instanceof Error ? err.message : "Reset failed",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      // Backend owns the embedded catalog — UI only scopes by productCode.
      const response = await seedTooltips({ productCode });
      if (!response.ok) {
        showAlert(
          response.status === 403
            ? "Forbidden — implementation team, TPM, or admin required."
            : `Seed failed (HTTP ${response.status}).`,
          "error",
        );
        return;
      }
      const seedResult = await parseTooltipSeedResponse(response);
      const inserted = seedResult?.inserted;
      const alreadyPresent = seedResult?.alreadyPresent;
      const countParts: string[] = [];
      if (typeof inserted === "number") {
        countParts.push(`inserted ${inserted}`);
      }
      if (typeof alreadyPresent === "number") {
        countParts.push(`already present ${alreadyPresent}`);
      }
      const codes =
        seedResult?.productCodes?.length
          ? seedResult.productCodes.join(", ")
          : productCode;
      showAlert(
        seedResult?.message?.trim() ||
          (countParts.length > 0
            ? `Seeded ${codes} (${countParts.join(", ")}).`
            : `Seeded ${codes} tooltip definitions (idempotent).`),
        "success",
      );
      setNeedsSeed(false);
      await loadRows(productCode);
      await refreshRuntime();
    } catch (err) {
      showAlert(
        err instanceof Error ? err.message : "Seed failed",
        "error",
      );
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Box className="tooltip-admin-page">
      <header className="tooltip-admin-header">
        <h1 className="tooltip-admin-title">Tooltip Admin — {productCode}</h1>
        <p className="tooltip-admin-subtitle">
          Edit instructional copy for <strong>{productCode}</strong> only.
          Overrides are global for this product (not per tenant). Use{" "}
          <strong>Reset to default</strong> to clear an override.
        </p>
      </header>

      {needsSeed && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          No tooltip rows for <strong>{productCode}</strong> yet. Showing static
          defaults — click <strong>Seed {productCode} catalog</strong> once to
          populate the OnBoarding tooltip tables.
        </Alert>
      )}

      <div className="tooltip-admin-toolbar">
        <TextField
          className="tooltip-admin-toolbar-search"
          size="small"
          placeholder="Search group, field, or text…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl
          size="small"
          className="tooltip-admin-toolbar-filter"
          sx={{ minWidth: 200 }}
        >
          <InputLabel id="tooltip-screen-filter-label">Screen</InputLabel>
          <Select
            labelId="tooltip-screen-filter-label"
            label="Screen"
            value={screenFilter}
            onChange={(e) => setScreenFilter(String(e.target.value))}
            startAdornment={
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <FilterListIcon fontSize="small" sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            }
          >
            <MenuItem value="">
              <em>All screens</em>
            </MenuItem>
            {screenOptions.map((screen) => (
              <MenuItem key={screen} value={screen}>
                {screen}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="tooltip-admin-toolbar-actions">
          <Button
            className="tooltip-admin-btn-seed"
            variant={needsSeed ? "contained" : "outlined"}
            color={needsSeed ? "primary" : "inherit"}
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => void handleSeed()}
            disabled={seeding || saving}
          >
            {seeding ? "Seeding…" : `Seed ${productCode}`}
          </Button>
          <Button
            className="tooltip-admin-btn-save"
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => void handleSave()}
            disabled={saving || dirtyCount === 0}
          >
            {saving ? "Saving…" : "Save"}
            {dirtyCount > 0 && (
              <span className="tooltip-admin-count">{dirtyCount}</span>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Loading {productCode} tooltips…
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No tooltips for {productCode}. Run Seed {productCode} if the API is
          available.
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No tooltips match
          {screenFilter ? ` “${screenFilter}”` : ""}
          {search.trim() ? ` for “${search.trim()}”` : ""}. Clear the screen
          filter or search to see more.
        </Alert>
      ) : (
        <>
        <div className="tooltip-admin-list">
          {pagedRows.map((row) => {
            const differs = row.draftText !== row.defaultText;
            const cardClass = [
              "tooltip-admin-card",
              row.dirty ? "tooltip-admin-card--dirty" : "",
              row.hasOverride ? "tooltip-admin-card--override" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <article
                key={`${row.groupKey}.${row.fieldKey}`}
                className={cardClass}
              >
                <div className="tooltip-admin-card-top">
                  <TooltipBreadcrumb
                    groupKey={row.groupKey}
                    fieldKey={row.fieldKey}
                  />
                  <div className="tooltip-admin-meta">
                    {row.hasOverride && (
                      <Chip
                        size="small"
                        label="Override"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 24, fontWeight: 600 }}
                      />
                    )}
                    {row.dirty && (
                      <Chip
                        size="small"
                        label="Unsaved"
                        color="info"
                        variant="outlined"
                        sx={{ height: 24, fontWeight: 600 }}
                      />
                    )}
                    <span className="tooltip-admin-key">
                      {row.groupKey}.{row.fieldKey}
                    </span>
                  </div>
                </div>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  value={row.draftText}
                  onChange={(e) =>
                    setDraft(row.groupKey, row.fieldKey, e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: "#fafafa",
                    },
                  }}
                />

                <div className="tooltip-admin-card-footer">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      className="tooltip-admin-btn-reset"
                      size="small"
                      variant="outlined"
                      startIcon={<RestartAltIcon fontSize="small" />}
                      onClick={() => void handleReset(row)}
                      disabled={saving || (!row.hasOverride && !differs && !row.dirty)}
                    >
                      Reset to default
                    </Button>
                    {differs && (
                      <span className="tooltip-admin-hint">
                        Differs from static default
                      </span>
                    )}
                  </Stack>
                  {row.updatedByUsername && (
                    <Typography variant="caption" color="text.secondary">
                      Last edit: {row.updatedByUsername}
                      {row.updatedAt ? ` · ${row.updatedAt}` : ""}
                    </Typography>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <Box className="tooltip-admin-pagination">
          <Typography variant="body2" color="text.secondary">
            {rangeStart}–{rangeEnd} of {filtered.length}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Per page
            </Typography>
            <FormControl size="small">
              <Select
                value={pageSize}
                onChange={(e) =>
                  setPageSize(
                    Number(e.target.value) as (typeof PAGE_SIZE_OPTIONS)[number],
                  )
                }
                sx={{ minWidth: 72 }}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={totalPages}
            page={safePage}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
        </>
      )}

      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Box>
  );
}
